<script setup>
import { reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();
const store = useStore();

const formState = {
  uiFlags: useMapGetter('buhotextCampaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  devices: useMapGetter('buhotextCampaigns/getDevices'),
};

const initialState = props.campaign
  ? {
      name: props.campaign.name,
      message: props.campaign.message,
      device_code: props.campaign.device_code,
      scheduled_at: props.campaign.scheduled_at
        ? new Date(props.campaign.scheduled_at).toISOString().slice(0, 16)
        : null,
      labels_filter: props.campaign.labels_filter || [],
    }
  : {
      name: '',
      message: '',
      device_code: null,
      scheduled_at: null,
      labels_filter: [],
    };

const state = reactive({ ...initialState });

const rules = {
  name: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(10) },
  device_code: { required },
  scheduled_at: {},
  labels_filter: { required },
};

const v$ = useVuelidate(rules, state);

const isSubmitting = computed(
  () => formState.uiFlags.value.isCreating || formState.uiFlags.value.isUpdating
);

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const deviceOptions = computed(() =>
  formState.devices.value.map(device => ({
    value: device.code,
    label: `${device.name} (${device.phone})`,
  }))
);

const labelsOptions = computed(() =>
  formState.labels.value.map(label => ({
    value: label.title,
    label: label.title,
  }))
);

const getErrorMessage = field => {
  if (v$.value[field].$error) {
    return t(`BUHOTEXT_CAMPAIGNS.FORM.${field.toUpperCase()}.ERROR`);
  }
  return '';
};

const formErrors = computed(() => ({
  name: getErrorMessage('name'),
  message: getErrorMessage('message'),
  device_code: getErrorMessage('device_code'),
  labels_filter: getErrorMessage('labels_filter'),
}));

const isSubmitDisabled = computed(() => v$.value.$invalid);

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  const formData = {
    name: state.name,
    message: state.message,
    device_code: state.device_code,
    scheduled_at: state.scheduled_at || null,
    labels_filter: state.labels_filter,
  };

  emit('submit', formData);
};

onMounted(() => {
  store.dispatch('buhotextCampaigns/fetchDevices');
  store.dispatch('labels/get');
});
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.name"
      :label="$t('BUHOTEXT_CAMPAIGNS.FORM.NAME.LABEL')"
      :placeholder="$t('BUHOTEXT_CAMPAIGNS.FORM.NAME.PLACEHOLDER')"
      :error="formErrors.name"
    />

    <ComboBox
      v-model="state.device_code"
      :label="$t('BUHOTEXT_CAMPAIGNS.FORM.DEVICE_CODE.LABEL')"
      :placeholder="$t('BUHOTEXT_CAMPAIGNS.FORM.DEVICE_CODE.PLACEHOLDER')"
      :options="deviceOptions"
      :error="formErrors.device_code"
    />

    <TagMultiSelectComboBox
      v-model="state.labels_filter"
      :label="$t('BUHOTEXT_CAMPAIGNS.FORM.LABELS_FILTER.LABEL')"
      :placeholder="$t('BUHOTEXT_CAMPAIGNS.FORM.LABELS_FILTER.PLACEHOLDER')"
      :options="labelsOptions"
      :error="formErrors.labels_filter"
    />

    <TextArea
      v-model="state.message"
      :label="$t('BUHOTEXT_CAMPAIGNS.FORM.MESSAGE.LABEL')"
      :placeholder="$t('BUHOTEXT_CAMPAIGNS.FORM.MESSAGE.PLACEHOLDER')"
      :error="formErrors.message"
      rows="5"
    />

    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        {{ $t('BUHOTEXT_CAMPAIGNS.FORM.SCHEDULED_AT.LABEL') }}
      </label>
      <input
        v-model="state.scheduled_at"
        type="datetime-local"
        :min="currentDateTime"
        class="px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9"
      />
      <span class="text-xs text-n-slate-11">
        {{ $t('BUHOTEXT_CAMPAIGNS.FORM.SCHEDULED_AT.HELP') }}
      </span>
    </div>

    <div class="flex gap-2 justify-end mt-2">
      <Button
        :label="$t('BUHOTEXT_CAMPAIGNS.FORM.CANCEL')"
        slate
        @click="emit('cancel')"
      />
      <Button
        :label="
          campaign
            ? $t('BUHOTEXT_CAMPAIGNS.FORM.UPDATE')
            : $t('BUHOTEXT_CAMPAIGNS.FORM.CREATE')
        "
        :disabled="isSubmitDisabled"
        :loading="isSubmitting"
        type="submit"
      />
    </div>
  </form>
</template>
